<script lang="ts" setup>
import { computed } from 'vue';

import type { AffiliateConditionFormSchema } from '@modules/affiliate-reference/types';
import type { VFormFilterButtonProps } from '@modules/form-widgets/components/VFormFilterButton/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import {
  AFFILIATE_REFERENCE_ROUTE_NAME,
  TRANSLATION_GROUP_AFFILIATE_REFERENCE,
} from '@modules/affiliate-reference/constants';
import { useAffiliateReferenceConditionQuery } from '@modules/affiliate-reference/queries';
import VFormButtonsGroupWidget
  from '@modules/form-widgets/components/VFormButtonsGroupWidget/VFormButtonsGroupWidget.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

const { getTranslation } = useI18n();

const { data, isLoading } = useAffiliateReferenceConditionQuery();

const formSchema = computed<FormSchema<AffiliateConditionFormSchema, 'subaffiliate_condition_steps_attributes'>>(() => ({
  title: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_REFERENCE, 'FORM_TITLE').value,
    value: data.value?.subaffiliate_condition.title,
    readonly: true,
  },
  description: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_REFERENCE, 'FORM_DESCRIPTION').value,
    value: data.value?.subaffiliate_condition.description,
    readonly: true,
  },
  subaffiliate_condition_steps_attributes: {
    type: EFormInputTypes.SLOT,
    readonly: true,
  },
}));

const buttons = computed<VFormFilterButtonProps[] | []>(() => {
  if (!data.value) {
    return [];
  }

  return data.value.subaffiliate_condition.subaffiliate_condition_steps_attributes.map((item) => ({
    type: 'primary',
    label: `${item.payment_size}% ${item.payment_column}`,
  }));
});
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliateReferenceConditionPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_REFERENCE, 'CONDITION_HEADER')"
  >
    <VLoader
      :loading="isLoading"
      mode="overlay"
    >
      <VForm
        :schema="formSchema"
      >
        <template #subaffiliate_condition_steps_attributes>
          <VFormButtonsGroupWidget
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_REFERENCE, 'STEP')"
            :buttons="buttons"
            readonly
          />
        </template>
        <template #secondaryBtn>
          <VBackButton
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_REFERENCE, 'BACK')"
            :to="{ name: AFFILIATE_REFERENCE_ROUTE_NAME }"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
